import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Layout from "../components/Layout";
import AiOutputDisclaimer from "../components/AiOutputDisclaimer";
import { api } from "../lib/api";
import { renderMarkdown } from "../lib/markdown";
import { errMsg } from "../lib/errors";
import { CASE_STATUS_STYLE, STATUS_STYLE, buildPortalUrl } from "../lib/collab";
import {
  Loader2, Users, Copy, CheckCheck, Trash2, ShieldCheck, Sparkles, AlertTriangle, Clock,
} from "lucide-react";
import { toast } from "sonner";

const ACK_TEXT =
  "I have reviewed all specialist recommendations and the interdisciplinary treatment plan. " +
  "My clinical decisions regarding treatment sequencing and patient communication are my sole " +
  "professional responsibility.";

export default function CollabCaseDetail() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [ack, setAck] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [reviewBusy, setReviewBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/collab/cases/${id}`);
      setC(r.data);
    } catch (err) {
      toast.error(errMsg(err, "Could not load case"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, [id]);

  const copyLink = async (token, inviteId) => {
    const url = buildPortalUrl(token);
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      window.prompt("Copy this specialist link", url);
    }
    setCopiedId(inviteId);
    setTimeout(() => setCopiedId(null), 1500);
  };

  const revoke = async (inviteId) => {
    if (!window.confirm("Revoke this specialist's link? They will no longer be able to submit.")) return;
    try {
      await api.delete(`/collab/cases/${id}/invites/${inviteId}`);
      await load();
      toast.success("Invite revoked");
    } catch (err) {
      toast.error(errMsg(err, "Could not revoke"));
    }
  };

  const synthesize = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/collab/cases/${id}/synthesize`);
      setC(r.data);
      toast.success("Interdisciplinary plan generated");
    } catch (err) {
      toast.error(errMsg(err, "Synthesis failed"));
    } finally {
      setBusy(false);
    }
  };

  const acknowledge = async () => {
    if (!ack) {
      toast.error("Please tick the acknowledgment box");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/collab/cases/${id}/acknowledge`, { acknowledged: true });
      setC(r.data);
      toast.success("Case acknowledged and finalized");
    } catch (err) {
      toast.error(errMsg(err, "Could not acknowledge"));
    } finally {
      setBusy(false);
    }
  };

  const requestTier4Review = async () => {
    setReviewBusy(true);
    try {
      const r = await api.post("/reviews/request-tiered", {
        tier_slug: "interdisciplinary",
        resource_id: id,
        origin_url: window.location.origin,
      });
      if (r.data.checkout_url) {
        window.location.href = r.data.checkout_url;
      } else {
        toast.success("Tier 4 review requested");
        load();
      }
    } catch (err) {
      toast.error(errMsg(err, "Could not request Tier 4 review"));
    } finally {
      setReviewBusy(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center gap-2 text-[var(--pa-text-muted)]">
          <Loader2 size={14} className="animate-spin text-[var(--pa-gold)]" /> Loading…
        </div>
      </Layout>
    );
  }
  if (!c) return <Layout>Not found.</Layout>;

  const status = CASE_STATUS_STYLE[c.status] || CASE_STATUS_STYLE.pending;
  const submitted = c.invites.filter((i) => i.status === "submitted");
  const allDone = c.invites.length > 0 && c.invites.every(
    (i) => i.status === "submitted" || i.status === "revoked" || i.status === "expired"
  );

  return (
    <Layout>
      <div className="flex items-center gap-3">
        <Users size={20} className="text-[var(--pa-gold)]" />
        <div>
          <div className={`kicker ${status.color}`}>{status.label}</div>
          <h1
            data-testid="collab-detail-title"
            className="font-serif text-3xl text-[var(--pa-text)]"
          >
            Interdisciplinary Case
          </h1>
        </div>
        <Link
          to={`/case-analysis/${c.case_analysis_id}`}
          className="ml-auto text-xs text-[var(--pa-gold)] hover:underline"
          data-testid="collab-detail-original-link"
        >
          ← View original Complete Case Analysis
        </Link>
      </div>

      {/* Timeline */}
      <section className="mt-8" data-testid="collab-timeline">
        <div className="kicker mb-3">Specialist Timeline</div>
        <div className="space-y-2.5">
          {c.invites.map((inv) => {
            const st = STATUS_STYLE[inv.status] || STATUS_STYLE.pending;
            const sub = c.submissions.find((s) => s.invite_id === inv.id);
            return (
              <div
                key={inv.id}
                data-testid={`collab-invite-${inv.id}`}
                className="p-4 bg-[var(--pa-surface)] border border-[var(--pa-border)] rounded-md"
              >
                <div className="flex items-center gap-3">
                  <span className={`w-2 h-2 rounded-full ${st.dot}`} />
                  <div className="flex-1">
                    <div className="text-sm text-[var(--pa-text)]">
                      {inv.specialty_label}
                    </div>
                    <div className={`text-[10px] uppercase tracking-wider mt-0.5 ${st.color}`}>
                      {st.label} · expires {new Date(inv.expires_at).toLocaleDateString()}
                    </div>
                  </div>
                  {inv.status === "pending" && (
                    <>
                      <button
                        onClick={() => copyLink(inv.token, inv.id)}
                        data-testid={`collab-copy-${inv.id}`}
                        className="flex items-center gap-1.5 text-[11px] px-2.5 py-1.5 rounded border border-[var(--pa-border)] hover:border-[var(--pa-gold)] text-[var(--pa-text)] hover:text-[var(--pa-gold)] transition"
                      >
                        {copiedId === inv.id ? <CheckCheck size={11} /> : <Copy size={11} />}
                        {copiedId === inv.id ? "Copied!" : "Copy link"}
                      </button>
                      <button
                        onClick={() => revoke(inv.id)}
                        data-testid={`collab-revoke-${inv.id}`}
                        className="p-1.5 rounded text-[var(--pa-text-dim)] hover:text-red-400 hover:bg-[var(--pa-bg-2)]"
                        title="Revoke link"
                      >
                        <Trash2 size={12} />
                      </button>
                    </>
                  )}
                </div>
                {sub && (
                  <div className="mt-3 ml-5 pl-3 border-l-2 border-[var(--pa-gold)]/40 text-xs text-[var(--pa-text-muted)]">
                    <div className="text-[var(--pa-text)] text-sm">
                      Dr. {sub.specialist_name}
                      {sub.specialist_license && (
                        <span className="text-[10px] text-[var(--pa-text-dim)] ml-2">
                          License {sub.specialist_license}
                        </span>
                      )}
                    </div>
                    <div className="text-[10px] text-[var(--pa-text-dim)] mt-0.5">
                      <Clock size={9} className="inline mr-1 -mt-0.5" />
                      submitted {new Date(sub.submitted_at).toLocaleString()}
                    </div>
                    {sub.timeline_estimate && (
                      <div className="text-[var(--pa-text)] mt-2">
                        Timeline: {sub.timeline_estimate}
                      </div>
                    )}
                    <details className="mt-2">
                      <summary className="cursor-pointer text-[var(--pa-gold)] hover:underline">
                        Read clinical notes
                      </summary>
                      <div
                        className="prose-clinical mt-2 text-xs"
                        dangerouslySetInnerHTML={{ __html: renderMarkdown(sub.notes_markdown) }}
                      />
                      {sub.treatment_sequence && (
                        <div className="mt-2">
                          <div className="kicker text-[var(--pa-text-dim)]">
                            Proposed sequence
                          </div>
                          <div
                            className="prose-clinical text-xs"
                            dangerouslySetInnerHTML={{ __html: renderMarkdown(sub.treatment_sequence) }}
                          />
                        </div>
                      )}
                    </details>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* Tier 4 — Comprehensive Interdisciplinary Review CTA. Surfaces once synthesis
          is generated; review request is bound to this collab case ($395). */}
      {c.synthesis_markdown && !c.acknowledged_at && (
        <div
          data-testid="collab-spec-review-cta"
          className="mt-6 p-5 bg-[var(--pa-gold)]/10 border-2 border-[var(--pa-gold)] rounded-md"
        >
          <div className="flex items-start gap-3">
            <ShieldCheck size={20} className="text-[var(--pa-gold)] mt-0.5 shrink-0" />
            <div className="flex-1 text-xs text-[var(--pa-text)] leading-relaxed">
              <div className="kicker text-[var(--pa-gold)]">Tier 4 · Comprehensive Interdisciplinary Review</div>
              <p className="mt-1.5">
                <strong>This is a complex interdisciplinary case.</strong> A comprehensive
                review by Dr. Ronik Seecharan DMD ensures the synthesized treatment plan is
                restorative-driven, correctly sequenced, and co-signed under prosthodontic
                oversight.
              </p>
              <p className="mt-2 italic text-[var(--pa-text-muted)]">
                At $395, a Comprehensive Interdisciplinary Review from Dr. Ronik Seecharan
                DMD is less than 3% of the average full-mouth rehabilitation case fee — and
                the most cost-effective specialist consultation in dentistry.
              </p>
            </div>
            <button
              onClick={requestTier4Review}
              disabled={reviewBusy}
              data-testid="collab-spec-review-btn"
              className="bg-[var(--pa-gold)] text-[var(--pa-bg)] text-xs font-semibold px-4 py-2.5 rounded hover:bg-[var(--pa-gold-hover)] transition whitespace-nowrap disabled:opacity-60"
            >
              {reviewBusy ? "Redirecting…" : "Request Tier 4 Review — $395"}
            </button>
          </div>
        </div>
      )}

      {/* Synthesize trigger */}
      {!c.synthesis_markdown && (
        <section className="mt-8" data-testid="collab-synth-trigger">
          <div className="p-5 bg-[var(--pa-surface)] border border-[var(--pa-border)] rounded-md">
            <h3 className="font-serif text-lg text-[var(--pa-text)]">
              Ready for synthesis?
            </h3>
            <p className="text-xs text-[var(--pa-text-muted)] mt-1.5 max-w-xl">
              ProsthoAI will read every specialist submission together with your original
              Complete Case Analysis and generate one comprehensive interdisciplinary
              treatment plan. You can synthesize once all requested specialists have submitted —
              or earlier if you want a preliminary plan from what you have.
            </p>
            <div className="text-[10px] text-[var(--pa-text-dim)] mt-2">
              {submitted.length} of {c.invites.length} submitted
              {allDone && " · all complete"}
            </div>
            <button
              onClick={synthesize}
              disabled={busy || submitted.length === 0}
              data-testid="collab-synth-btn"
              className="mt-4 flex items-center gap-2 bg-[var(--pa-gold)] text-[var(--pa-bg)] font-semibold px-4 py-2.5 rounded hover:bg-[var(--pa-gold-hover)] transition disabled:opacity-60"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
              {busy ? "Synthesizing…" : "Run interdisciplinary synthesis"}
            </button>
            {submitted.length === 0 && (
              <p className="text-[10px] text-amber-300 mt-2">
                <AlertTriangle size={10} className="inline mr-1 -mt-0.5" />
                At least one specialist submission is required.
              </p>
            )}
          </div>
        </section>
      )}

      {/* Synthesis */}
      {c.synthesis_markdown && (
        <section className="mt-8" data-testid="collab-synthesis">
          <div className="kicker text-[var(--pa-gold)]">Interdisciplinary Treatment Plan</div>
          <h2 className="font-serif text-2xl text-[var(--pa-text)] mt-1">
            Synthesis by ProsthoAI
          </h2>
          <div className="text-[10px] text-[var(--pa-text-dim)] mt-1">
            Generated {new Date(c.synthesis_generated_at).toLocaleString()}
          </div>
          <AiOutputDisclaimer className="mt-4" />
          <div
            className="prose-clinical mt-5 p-6 bg-[var(--pa-surface)] border border-[var(--pa-border-active)] rounded-lg"
            dangerouslySetInnerHTML={{ __html: renderMarkdown(c.synthesis_markdown) }}
          />

          {!c.acknowledged_at && (
            <div
              className="mt-6 p-5 bg-amber-500/5 border border-amber-500/40 rounded-md"
              data-testid="collab-ack-block"
            >
              <div className="kicker text-amber-300 mb-2">Clinician acknowledgment required</div>
              <label className="flex items-start gap-3 text-sm text-[var(--pa-text)] cursor-pointer">
                <input
                  type="checkbox"
                  checked={ack}
                  onChange={(e) => setAck(e.target.checked)}
                  data-testid="collab-ack-checkbox"
                  className="mt-1 accent-[var(--pa-gold)]"
                />
                <span className="leading-relaxed">{ACK_TEXT}</span>
              </label>
              <button
                onClick={acknowledge}
                disabled={!ack || busy}
                data-testid="collab-ack-submit"
                className="mt-4 bg-[var(--pa-gold)] text-[var(--pa-bg)] font-semibold rounded px-5 py-2.5 hover:bg-[var(--pa-gold-hover)] transition disabled:opacity-60"
              >
                {busy ? "Saving…" : "Acknowledge & finalize case"}
              </button>
            </div>
          )}

          {c.acknowledged_at && (
            <div
              className="mt-6 p-4 bg-emerald-500/5 border border-emerald-500/40 rounded-md text-xs text-[var(--pa-text-muted)]"
              data-testid="collab-acknowledged"
            >
              <ShieldCheck size={14} className="inline text-emerald-400 mr-1.5 -mt-0.5" />
              Acknowledged by the clinician on{" "}
              {new Date(c.acknowledged_at).toLocaleString()}. Case finalized.
            </div>
          )}
        </section>
      )}
    </Layout>
  );
}
